import React, { useMemo, useRef } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  parseBeaconBuyBoxSellersResponse,
  useBeaconBuyBoxSellersData
} from 'src/components/BeaconV2Components/ExperimentalLayout/BuyBox/Sellers/useBeaconBuyBoxSellersData';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import BuyBoxLossRateByProductGrid from './BuyBoxLossRateByProductGrid';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import BuyBoxByWeekGrid from 'src/components/BeaconV2Components/ExperimentalLayout/BuyBox/Summary/BuyBoxByWeekGrid';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';

const BuyBoxSellersPage = () => {
  const { isLoading, data } = useBeaconBuyBoxSellersData();
  const entityType = useMainEntityType();

  const sellerBuyBoxData = useMemo(() => {
    return data ? parseBeaconBuyBoxSellersResponse(data) : [];
  }, [data]);

  const theme = useStacklineTheme();
  const saveImageRef = useRef<HTMLDivElement>(null);

  return (
    <BasePageContainer>
      <Flex gap={BEACON_CHART_SPACING} flexDirection="column">
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_BUYBOX_SELLERS.name}
          chartName={CHART_NAMES.BEACON_BUYBOX_SELLERS_COLUMN}
        >
          <BeaconChartWithLegend
            title="Sellers"
            loading={isLoading}
            convertSeriesToDelimitedData={async () => {
              saveSingleBarChartAsDelimitedData(sellerBuyBoxData, {
                fileTitle: `BuyBoxSellers-${entityType}`
              });
            }}
          >
            <div ref={saveImageRef}>
              <TopEntityChartV2
                chartSeries={[
                  {
                    type: 'column',
                    data: sellerBuyBoxData,
                    metricType: METRICTYPE.PERCENT
                  }
                ]}
                seriesColors={[theme.colors.primary]}
                chartOptionsOverride={{
                  chart: {
                    height: 300
                  }
                }}
              />
            </div>
          </BeaconChartWithLegend>
        </ChartContextProvider>
        {entityType !== 'product' && <BuyBoxLossRateByProductGrid />}
        {entityType === 'product' && (
          <BuyBoxByWeekGrid
            gridTitle="Loss Rate by Week"
            fields={[
              {
                name: 'buyBoxWeighted',
                displayInTable: false,
                comparisonFormatter: () => null
              },
              {
                name: 'winsByClient',
                displayInTable: false,
                comparisonFormatter: () => null
              },
              {
                name: 'lossPercentage',
                displayName: 'Buy Box - Loss Rate',
                comparisonFormatter: () => null
              },
              {
                name: 'winPercentage',
                displayName: 'Buy Box - Win Rate',
                comparisonFormatter: () => null
              }
            ]}
          />
        )}
      </Flex>
    </BasePageContainer>
  );
};

export default BuyBoxSellersPage;
